/**
 * Custom wrapper around React Query's useMutation hook
 * Provides consistent error handling, success notifications, and cache invalidation
 */

import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
  QueryKey,
  MutationFunctionContext,
} from '@tanstack/react-query';
import { useSafeToast } from '@/hooks/useSafeToast';
import { APIError } from '@/utils/api-client';

interface UseApiMutationOptions<
  TData = unknown,
  TError = APIError,
  TVariables = void,
  TContext = unknown,
> extends Omit<
  UseMutationOptions<TData, TError, TVariables, TContext>,
  'mutationFn'
> {
  invalidateQueries?: QueryKey[];
  showSuccessToast?: boolean;
  showErrorToast?: boolean;
  successMessage?: string | ((data: TData) => string);
  errorMessage?: string;
  onSuccessCallback?: (
    data: TData,
    variables: TVariables
  ) => void | Promise<void>;
  // Allow passing custom invalidateKeys function for dynamic invalidation
  invalidateKeys?:
    QueryKey[] | ((data: TData, variables: TVariables) => QueryKey[]);
  // Add mutation key for better DevTools experience
  mutationKey?: QueryKey;
}

export function useApiMutation<
  TData = unknown,
  TError = APIError,
  TVariables = void,
  TContext = unknown,
>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options?: UseApiMutationOptions<TData, TError, TVariables, TContext>
): UseMutationResult<TData, TError, TVariables, TContext> {
  const queryClient = useQueryClient();
  const { toast } = useSafeToast();

  const {
    invalidateQueries = [],
    invalidateKeys,
    showSuccessToast = true,
    showErrorToast = true,
    successMessage,
    errorMessage,
    onSuccessCallback,
    mutationKey,
    ...mutationOptions
  } = options || {};

  return useMutation({
    mutationFn,
    mutationKey,
    ...mutationOptions,
    onSuccess: async (
      data: TData,
      variables: TVariables,
      onMutateResult: TContext,
      context: MutationFunctionContext
    ) => {
      // Call the original onSuccess if provided
      await options?.onSuccess?.(data, variables, onMutateResult, context);

      // Handle invalidateKeys (new pattern)
      if (invalidateKeys) {
        const keysToInvalidate =
          typeof invalidateKeys === 'function'
            ? invalidateKeys(data, variables)
            : invalidateKeys;

        await Promise.all(
          keysToInvalidate.map((queryKey) =>
            queryClient.invalidateQueries({ queryKey })
          )
        );
      }

      // Handle invalidateQueries (old pattern - for backward compatibility)
      else if (invalidateQueries.length > 0) {
        await Promise.all(
          invalidateQueries.map((queryKey) =>
            queryClient.invalidateQueries({ queryKey })
          )
        );
      }

      // Show success toast if enabled
      if (showSuccessToast) {
        const message =
          typeof successMessage === 'function'
            ? successMessage(data)
            : successMessage || 'Operation completed successfully';

        toast({
          title: 'Success',
          description: message,
        });
      }

      // Call additional success callback
      if (onSuccessCallback) {
        await onSuccessCallback(data, variables);
      }
    },
    onError: (
      error: TError,
      variables: TVariables,
      onMutateResult: TContext | undefined,
      context: MutationFunctionContext
    ) => {
      // Call the original onError if provided
      options?.onError?.(error, variables, onMutateResult, context);

      // Show error toast if enabled, unless the error is a field-level
      // validation error the caller is expected to render inline
      const hasFieldErrors =
        error instanceof APIError && !!error.validationErrors;
      if (showErrorToast && !hasFieldErrors) {
        const message =
          errorMessage ||
          (error instanceof APIError ? error.message : 'An error occurred');

        toast({
          title: 'Error',
          description: message,
          variant: 'destructive',
        });
      }
    },
  });
}

// Helper for common CRUD operations
interface CrudMutationHelpers<TData, TCreateVariables, TUpdateVariables> {
  create: UseMutationResult<TData, APIError, TCreateVariables, unknown>;
  update: UseMutationResult<
    TData,
    APIError,
    TUpdateVariables & { id: string },
    unknown
  >;
  delete: UseMutationResult<void, APIError, string, unknown>;
}

export function useCrudMutations<TData, TCreateVariables, TUpdateVariables>(
  entityName: string,
  mutations: {
    create: (variables: TCreateVariables) => Promise<TData>;
    update: (variables: TUpdateVariables & { id: string }) => Promise<TData>;
    delete: (id: string) => Promise<void>;
  },
  invalidateQueries: QueryKey[]
): CrudMutationHelpers<TData, TCreateVariables, TUpdateVariables> {
  const createMutation = useApiMutation(mutations.create, {
    invalidateQueries,
    successMessage: `${entityName} created successfully`,
  });

  const updateMutation = useApiMutation(mutations.update, {
    invalidateQueries,
    successMessage: `${entityName} updated successfully`,
  });

  const deleteMutation = useApiMutation(mutations.delete, {
    invalidateQueries,
    successMessage: `${entityName} deleted successfully`,
  });

  return {
    create: createMutation,
    update: updateMutation,
    delete: deleteMutation,
  };
}
